// safety/incidents-and-injuries/components/shared.tsx
import React, { useEffect, useState } from 'react';
import { X, Plus } from 'lucide-react';
import { statusPillClass } from '../utils/incidents-utils';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../../components/ui/select';
import type { IncidentStatus } from '../../../../types/incident.types';

/* ---------- Status pill ---------- */
export const StatusPill: React.FC<{ status: IncidentStatus }> = ({ status }) => (
    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusPillClass(status)}`}>
        {status}
    </span>
);

/* ---------- Info cell (read-only labelled value) ---------- */
export const InfoCell: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
    <div className="rounded-lg border border-border p-3">
        <div className="text-[11px] uppercase tracking-wide text-muted-foreground mb-1">{label}</div>
        <div className="text-sm font-medium">{children ?? '—'}</div>
    </div>
);

/* ---------- Section title with optional right action ---------- */
export const SectionTitle: React.FC<{ title: string; right?: React.ReactNode }> = ({ title, right }) => (
    <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold">{title}</h3>
        {right}
    </div>
);

/* ---------- Primary "Add X" button ---------- */
export const AddButton: React.FC<{ label: string; onClick: () => void }> = ({ label, onClick }) => (
    <button
        onClick={onClick}
        className="inline-flex items-center gap-1.5 h-9 px-3.5 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90"
    >
        <Plus size={16} />
        {label}
    </button>
);

/* ---------- Slide-over Drawer ---------- */
export const Drawer: React.FC<{
    open: boolean;
    width?: string;
    onClose: () => void;
    children: React.ReactNode;
}> = ({ open, width = '720px', onClose, children }) => {
    const [mounted, setMounted] = useState(open);
    useEffect(() => {
        if (open) setMounted(true);
        else {
            const t = setTimeout(() => setMounted(false), 280);
            return () => clearTimeout(t);
        }
    }, [open]);

    if (!mounted) return null;
    return (
        <>
            <div
                onClick={onClose}
                className="fixed inset-0 z-30 bg-black/40 transition-opacity duration-300"
                style={{ opacity: open ? 1 : 0 }}
            />
            <div
                className="fixed top-0 right-0 h-screen z-40 bg-card shadow-2xl flex flex-col transition-transform duration-300"
                style={{ width, transform: open ? 'translateX(0)' : 'translateX(100%)' }}
            >
                {children}
            </div>
        </>
    );
};

/* ---------- Reusable form modal ---------- */
export type FieldType = 'text' | 'number' | 'date' | 'select' | 'textarea' | 'yn';
export interface FormField {
    key: string;
    label: string;
    type?: FieldType;
    options?: string[];
    value?: any;
}

export const FormModal: React.FC<{
    title: string;
    fields: FormField[];
    onSubmit: (vals: Record<string, any>) => void;
    onClose: () => void;
}> = ({ title, fields, onSubmit, onClose }) => {
    const [vals, setVals] = useState<Record<string, any>>(() => {
        const init: Record<string, any> = {};
        fields.forEach((f) => (init[f.key] = f.value ?? (f.type === 'yn' ? false : '')));
        return init;
    });
    const set = (k: string, v: any) => setVals((p) => ({ ...p, [k]: v }));

    return (
        <div
            className="fixed inset-0 z-[90] bg-black/40 flex items-center justify-center p-4"
            onClick={(e) => e.target === e.currentTarget && onClose()}
        >
            <div className="bg-card rounded-lg border border-border shadow-xl w-full max-w-md max-h-[85vh] flex flex-col">
                <div className="flex items-center justify-between px-5 py-4 border-b border-border">
                    <h3 className="text-base font-semibold">{title}</h3>
                    <button onClick={onClose} className="text-muted-foreground hover:text-foreground">
                        <X size={20} />
                    </button>
                </div>
                <div className="px-5 py-4 overflow-y-auto space-y-4">
                    {fields.map((f) => (
                        <div key={f.key}>
                            <label className="text-sm font-medium">{f.label}</label>
                            <div className="mt-1.5">
                                {f.type === 'select' ? (
                                    <Select value={vals[f.key]} onValueChange={(v) => set(f.key, v)}>
                                        <SelectTrigger className="w-full bg-muted/40 border-border rounded-lg">
                                            <SelectValue placeholder="Select..." />
                                        </SelectTrigger>
                                        <SelectContent className="z-[120]">
                                            {(f.options ?? []).map((o) => (
                                                <SelectItem key={o} value={o}>{o}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                ) : f.type === 'textarea' ? (
                                    <textarea
                                        rows={3}
                                        value={vals[f.key]}
                                        onChange={(e) => set(f.key, e.target.value)}
                                        className="w-full rounded-lg border border-border bg-muted/40 px-3 py-2.5 text-sm"
                                    />
                                ) : f.type === 'yn' ? (
                                    <div className="flex gap-2">
                                        {[true, false].map((b) => (
                                            <button
                                                key={String(b)}
                                                type="button"
                                                onClick={() => set(f.key, b)}
                                                className={`h-10 px-5 rounded-lg text-sm font-medium border ${
                                                    vals[f.key] === b
                                                        ? 'bg-primary text-primary-foreground border-primary'
                                                        : 'border-border hover:bg-accent'
                                                }`}
                                            >
                                                {b ? 'Yes' : 'No'}
                                            </button>
                                        ))}
                                    </div>
                                ) : (
                                    <input
                                        type={f.type ?? 'text'}
                                        value={vals[f.key]}
                                        onChange={(e) => set(f.key, e.target.value)}
                                        className="w-full h-10 rounded-lg border border-border bg-muted/40 px-3 text-sm"
                                    />
                                )}
                            </div>
                        </div>
                    ))}
                </div>
                <div className="px-5 py-3.5 border-t border-border flex justify-end gap-2">
                    <button
                        onClick={onClose}
                        className="h-10 px-4 rounded-lg border border-border bg-card text-sm font-medium hover:bg-accent"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={() => onSubmit(vals)}
                        className="h-10 px-5 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90"
                    >
                        Save
                    </button>
                </div>
            </div>
        </div>
    );
};
